// Pestaña "Decisiones" (cerebro, etapa 1): reglas de "qué mostrar", sin React, para poder
// probarlas sueltas (scripts/decisions-view.test.ts). Igual que la Medición: nunca se muestra un
// error interno ni un nombre interno; ante la duda, un texto fijo en español u "Otro".
// Los pedidos, los fallos y los nombres de empresa usan las reglas de brainView.
import { nextList, type Fetched } from './brainView';

export const LIST_FAIL_KEPT =
  'No se pudo leer la lista de decisiones. Se muestra la última que se pudo leer.';
export const LIST_FAIL_NEVER = 'No se pudo leer la lista de decisiones.';
export const APPROVE_FAIL = 'No se pudo aprobar. Probá de nuevo en un momento.';
export const REJECT_FAIL = 'No se pudo rechazar. Probá de nuevo en un momento.';
/** Mismo texto que manda el servidor cuando no pudo comprobar en vivo: la propuesta sigue pendiente. */
export const UNVERIFIED_TEXT =
  'No se pudo verificar en Mercado Libre si sigue abierta. No se envió nada; probá en un momento.';

/**
 * Textos fijos del servidor cuando no pudo comprobar algo antes de enviar (propuestas.ts): la
 * propuesta SIGUE pendiente. El de arriba es el de preguntas y mensajes; estos, el de reclamos y el
 * de correos de reclamo de no entregadas.
 */
export const UNVERIFIED_TEXTS: ReadonlySet<string> = new Set([
  UNVERIFIED_TEXT,
  'No se pudo verificar el reclamo en Mercado Libre. No se hizo nada; probá en un momento.',
  'No se pudo comprobar si alguna de las ventas ya fue cobrada o devuelta. No se envió nada; probá en un momento.',
]);
export const SUPERSEDED_FALLBACK = 'Ya la respondieron por otra vía. No se envió nada.';

/** Texto de la tarjeta cuando quedó superada: el motivo que manda el servidor (texto fijo en español). */
export function supersededText(head: string, detail: string | null | undefined): string {
  const d = (detail ?? '').trim();
  return `${head}: ${d || SUPERSEDED_FALLBACK}`;
}
/** Texto de la tarjeta cuando no se pudo verificar: el del servidor si es uno de los conocidos. */
export function unverifiedText(detail: string | null | undefined): string {
  return detail && UNVERIFIED_TEXTS.has(detail) ? detail : UNVERIFIED_TEXT;
}

export const KIND_LABEL: Record<string, string> = {
  answer_question: 'Responder una pregunta',
  pack_send_message: 'Mensaje al comprador',
  send_email: 'Correo a Mercado Libre',
  reply_email: 'Respuesta a Mercado Libre',
  send_invoice: 'Mandar la factura',
};
/** Nota de la tarjeta "Mandar la factura": qué hace exactamente la herramienta. */
export const INVOICE_CARD_NOTE =
  'Se genera la factura en PDF con los datos de la venta, se adjunta en la conversación y se manda este texto fijo de la empresa. Si la mensajería está bloqueada por un reclamo, va por el reclamo. Si ya se mandó, no se repite.';
export const SITUATION_LABEL: Record<string, string> = {
  original_condicion: 'Originalidad / condición',
  disponibilidad_envio: 'Disponibilidad / envío',
  detalle_producto: 'Detalle del producto',
  precio_pago: 'Precio / pago',
  donde_esta_mi_pedido: 'Dónde está mi pedido',
  demora_entrega: 'Demora en la entrega',
  producto_danado_o_distinto: 'Producto dañado o distinto',
  factura_o_comprobante: 'Factura / comprobante',
  cancelar_o_devolver: 'Cancelar o devolver',
  agradecimiento_o_sin_pregunta: 'Agradecimiento / sin pregunta',
  reclamo_no_entregadas: 'Reclamo de no entregadas',
  rechazo_por_plazo: 'Rechazo por plazo (reclamo previo)',
  otro: 'Otro',
};
/** Motivos del "No": los mismos valores que acepta el servidor. */
export const REJECT_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'dato_equivocado', label: 'Tiene un dato equivocado' },
  { value: 'tono', label: 'El tono no va' },
  { value: 'no_corresponde', label: 'No corresponde responder esto' },
  { value: 'ya_resuelto', label: 'Ya está resuelto' },
  { value: 'otro', label: 'Otro motivo' },
];

const has = (m: Record<string, string>, k: string) => Object.prototype.hasOwnProperty.call(m, k);
export const kindLabel = (k: string) => (has(KIND_LABEL, k) ? KIND_LABEL[k] : 'Otro');
export const situationLabel = (s: string | null) =>
  s && has(SITUATION_LABEL, s) ? SITUATION_LABEL[s] : 'Otro';

/**
 * Una fila de la medición de veredictos (la columna "superada" incluye también las vencidas).
 * `failed` = aprobadas por el dueño cuyo envío falló: no cuentan como aprobadas.
 */
export interface StatCounts {
  proposed: number;
  approved_as_is: number;
  approved_edited: number;
  failed?: number;
  rejected: number;
  superseded: number;
}

const pct = (n: number, total: number) => (total > 0 ? Math.round((n / total) * 100) : 0);
const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/** Texto de la cabecera de medición: "12 propuestas · 7 tal cual (58 %) · 3 con cambios (25 %) · 1 rechazada (8 %) · 1 por otra vía o vencida". */
export function statsLine(rows: StatCounts[]): string {
  const t = rows.reduce<Required<StatCounts>>(
    (a, r) => ({
      proposed: a.proposed + (Number(r.proposed) || 0),
      approved_as_is: a.approved_as_is + (Number(r.approved_as_is) || 0),
      approved_edited: a.approved_edited + (Number(r.approved_edited) || 0),
      rejected: a.rejected + (Number(r.rejected) || 0),
      failed: a.failed + (Number(r.failed) || 0),
      superseded: a.superseded + (Number(r.superseded) || 0),
    }),
    { proposed: 0, approved_as_is: 0, approved_edited: 0, rejected: 0, failed: 0, superseded: 0 }
  );
  if (t.proposed === 0) return 'Sin propuestas todavía.';
  return [
    `${t.proposed} ${plural(t.proposed, 'propuesta', 'propuestas')}`,
    `${t.approved_as_is} tal cual (${pct(t.approved_as_is, t.proposed)} %)`,
    `${t.approved_edited} con cambios (${pct(t.approved_edited, t.proposed)} %)`,
    `${t.rejected} ${plural(t.rejected, 'rechazada', 'rechazadas')} (${pct(t.rejected, t.proposed)} %)`,
    `${t.superseded} por otra vía o ${plural(t.superseded, 'vencida', 'vencidas')}`,
    ...(t.failed > 0
      ? [`${t.failed} ${plural(t.failed, 'aprobada que falló', 'aprobadas que fallaron')}`]
      : []),
  ].join(' · ');
}

/**
 * Decisiones tomadas por el dueño (tal cual + con cambios + rechazadas + aprobadas cuyo envío
 * falló: también las decidió); no cuenta las de otra vía.
 */
export function decidedCount(rows: StatCounts[]): number {
  return rows.reduce(
    (a, r) =>
      a +
      (Number(r.approved_as_is) || 0) +
      (Number(r.approved_edited) || 0) +
      (Number(r.failed) || 0) +
      (Number(r.rejected) || 0),
    0
  );
}

/** Una pregunta anterior del mismo comprador en esa publicación (con nuestra respuesta, si hubo). */
export interface PreviousQa {
  date: string | null;
  question: string;
  answer: string | null;
}

/**
 * Hilo previo del comprador guardado con la propuesta (`context.previous_qa`): lista de
 * {date, question, answer}. Cualquier otra forma (borradores viejos con otro formato, nulo, texto)
 * se ignora y devuelve lista vacía: nunca se muestra algo que no sea de este comprador y artículo.
 */
export function previousQa(ctx: Record<string, unknown> | null | undefined): PreviousQa[] {
  let v: unknown = ctx?.previous_qa;
  if (typeof v === 'string') {
    try {
      v = JSON.parse(v);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(v)) return [];
  return v
    .filter(
      (e): e is Record<string, unknown> =>
        !!e && typeof e === 'object' && typeof (e as { question?: unknown }).question === 'string'
    )
    .filter((e) => String(e.question).trim() !== '')
    .map((e) => ({
      date: typeof e.date === 'string' ? e.date : null,
      question: String(e.question),
      answer: typeof e.answer === 'string' && e.answer.trim() ? e.answer : null,
    }));
}

/** El ayudante encontró posibles productos para la pregunta pero ninguno quedó confirmado. */
export function productUnconfirmed(ctx: Record<string, unknown> | null | undefined): boolean {
  const c = ctx?.product_candidates;
  const confirmed = ctx?.product_confirmed_asin;
  return (
    Array.isArray(c) && c.length > 0 && (confirmed == null || String(confirmed).trim() === '')
  );
}
export const PRODUCT_UNCONFIRMED =
  'El producto de esta pregunta no está confirmado: puede ser otra variante. Revisá antes de aprobar.';

/**
 * Título del bloque de evidencia, según el TIPO de propuesta (no según si la lista viene vacía):
 * en una pregunta la evidencia es la comprobación en vivo que hace el sistema; en un mensaje es lo
 * que el modelo DICE que consultó (no un hecho comprobado por nosotros). Un tipo desconocido nunca
 * se presenta como comprobado.
 */
export function evidenceTitle(kind: string): string {
  if (kind === 'answer_question') return 'Comprobación en vivo del sistema';
  if (kind === 'send_email') return 'Qué se reclama';
  if (kind === 'reply_email') return 'Qué se responde';
  return 'Lo que dice que consultó';
}

const NOTE_BASE = 'El cerebro propone; no se envía nada hasta que digas que sí.';
const NOTE_ML = 'Antes de enviar una respuesta o un mensaje se vuelve a comprobar en Mercado Libre que siga sin responder.';
const NOTE_REPLY =
  'La respuesta sale en el mismo hilo del correo de Mercado Libre (así la toman como parte del caso).';
const NOTE_MAIL =
  'Antes de enviar un correo a Mercado Libre se comprueba que ninguna venta haya sido cobrada o devuelta.';
/**
 * Texto bajo el título de la pestaña según el filtro de tipo: la comprobación previa no es la misma
 * para preguntas/mensajes (en vivo en Mercado Libre) que para los correos de reclamo (la venta).
 */
export function headerNote(kind: string): string {
  if (kind === 'send_email') return `${NOTE_BASE} ${NOTE_MAIL}`;
  if (kind === 'reply_email') return `${NOTE_BASE} ${NOTE_REPLY}`;
  if (kind === 'answer_question' || kind === 'pack_send_message') return `${NOTE_BASE} ${NOTE_ML}`;
  if (kind === 'send_invoice')
    return `${NOTE_BASE} Antes de mandar la factura se comprueba que no se haya mandado ya.`;
  return `${NOTE_BASE} ${NOTE_ML} ${NOTE_MAIL}`;
}

/**
 * Correo a Mercado Libre (volver a reclamar no entregadas): líneas del costado de la tarjeta a partir
 * del contexto guardado con la propuesta ({to, subject, sales|orders, round, before}). Lo que no
 * viene, no se muestra; nunca importes.
 * Ej.: ['Para: inbounds.cbt@mercadolibre.com', 'Asunto: …', 'Ventas: 3 (ronda 2)',
 *       'Antes: 2 sin respuesta, 1 rechazada'].
 */
export function mailSideLines(ctx: Record<string, unknown> | null | undefined): string[] {
  const c = ctx ?? {};
  const out: string[] = [];
  const to = Array.isArray(c.to)
    ? (c.to as unknown[]).filter((x): x is string => typeof x === 'string' && x.trim() !== '')
    : typeof c.to === 'string' && c.to.trim()
      ? [c.to]
      : [];
  if (to.length) out.push(`Para: ${to.join(', ')}`);
  if (typeof c.subject === 'string' && c.subject.trim()) out.push(`Asunto: ${c.subject}`);
  const n = Number(c.sales ?? c.orders);
  const round = Number(c.round);
  if (Number.isFinite(n) && n > 0)
    out.push(`Ventas: ${n}${Number.isInteger(round) && round > 0 ? ` (ronda ${round})` : ''}`);
  const b = c.before && typeof c.before === 'object' ? (c.before as Record<string, unknown>) : {};
  const parts: string[] = [];
  const add = (k: string, one: string, many: string) => {
    const v = Number(b[k]);
    if (Number.isInteger(v) && v > 0) parts.push(`${v} ${v === 1 ? one : many}`);
  };
  add('sin_respuesta', 'sin respuesta', 'sin respuesta');
  add('rechazada', 'rechazada', 'rechazadas');
  add('rechazo_canal', 'con pedido de reclamar por país', 'con pedido de reclamar por país');
  if (parts.length) out.push(`Antes: ${parts.join(', ')}`);
  return out;
}

/**
 * País del comprador a partir del sitio de Mercado Libre (`site_id` en preguntas, `site` en
 * mensajes). La respuesta puede cambiar según el país (voltaje, envíos, idioma): va bien visible.
 */
export const COUNTRY_OF_SITE: Record<string, { flag: string; name: string }> = {
  MLA: { flag: '🇦🇷', name: 'Argentina' },
  MLB: { flag: '🇧🇷', name: 'Brasil' },
  MLM: { flag: '🇲🇽', name: 'México' },
  MLC: { flag: '🇨🇱', name: 'Chile' },
  MCO: { flag: '🇨🇴', name: 'Colombia' },
  MLU: { flag: '🇺🇾', name: 'Uruguay' },
  MPE: { flag: '🇵🇪', name: 'Perú' },
  MEC: { flag: '🇪🇨', name: 'Ecuador' },
  MLV: { flag: '🇻🇪', name: 'Venezuela' },
  MCR: { flag: '🇨🇷', name: 'Costa Rica' },
  MPA: { flag: '🇵🇦', name: 'Panamá' },
  MRD: { flag: '🇩🇴', name: 'Rep. Dominicana' },
  MGT: { flag: '🇬🇹', name: 'Guatemala' },
  MHN: { flag: '🇭🇳', name: 'Honduras' },
  MNI: { flag: '🇳🇮', name: 'Nicaragua' },
  MSV: { flag: '🇸🇻', name: 'El Salvador' },
  MBO: { flag: '🇧🇴', name: 'Bolivia' },
  MPY: { flag: '🇵🇾', name: 'Paraguay' },
};

export interface Country {
  code: string;
  flag: string;
  name: string;
}

/** País del comprador según el contexto de la propuesta; null si no viene. Sitio desconocido → sin bandera. */
export function countryOf(ctx: Record<string, unknown> | null | undefined): Country | null {
  const raw = ctx?.site_id ?? ctx?.site;
  if (typeof raw !== 'string' || !raw.trim()) return null;
  const code = raw.trim().toUpperCase();
  const c = Object.prototype.hasOwnProperty.call(COUNTRY_OF_SITE, code) ? COUNTRY_OF_SITE[code] : null;
  return c ? { code, flag: c.flag, name: c.name } : { code, flag: '', name: code };
}

export const COUNTRY_UNKNOWN = 'País sin datos';

export type ApproveOutcome = 'done' | 'superseded' | 'failed' | 'unverified' | 'kept';

/**
 * Clasificación del resultado de aprobar para la tarjeta:
 * - done: se envió (la tarjeta se va); superseded: ya la respondieron por otra vía (se va);
 * - unverified: no se pudo verificar en vivo (queda, con texto fijo);
 * - kept: el servidor no la ejecutó y la propuesta SIGUE pendiente (p. ej. texto editado que no
 *   pasa la validación): queda, con el motivo y la edición conservada;
 * - failed: falló y ya no está pendiente (se va; el asunto vuelve a la fila).
 */
export function approveOutcome(d: {
  executed: boolean;
  detail: string;
  superseded?: boolean;
  still_pending?: boolean;
}): ApproveOutcome {
  if (d.executed) return 'done';
  if (d.superseded) return 'superseded';
  if (UNVERIFIED_TEXTS.has(d.detail)) return 'unverified';
  if (d.still_pending) return 'kept';
  return 'failed';
}

/**
 * "Sí con cambios": texto a mandar como editado, o undefined si (sin espacios de los bordes) es
 * igual al propuesto — así cuenta como "tal cual".
 */
export function editedTextToSend(edited: string, proposed: string): string | undefined {
  return edited.trim() === proposed.trim() ? undefined : edited;
}

/** Tras pedir confirmación, los clics sobre "Sí, enviar" se ignoran este tiempo (evita el doble clic). */
export const CONFIRM_GUARD_MS = 500;
/** ¿Se acepta el clic de confirmar? Solo si pasó el tiempo de guardia desde que se armó. */
export function confirmClickAllowed(armedAt: number, now: number): boolean {
  return now - armedAt >= CONFIRM_GUARD_MS;
}

/** Fallo de red al aprobar: el envío pudo haberse hecho, así que no se invita a repetir. */
export const APPROVE_UNKNOWN = 'No se pudo confirmar si se envió. Se actualiza la lista.';

/** Validación local del texto editado antes de mandar: vacío, tope de letras. */
export function editProblem(text: string, maxLen: number): string | null {
  if (!text.trim()) return 'El texto está vacío.';
  if (text.length > maxLen) return `Se pasa del tope: ${text.length} de ${maxLen} letras.`;
  return null;
}

/** "hace 5 min", "hace 3 h", "hace 2 días" (fecha inválida → ""). */
export function agoText(iso: string, now: number = Date.now()): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const m = Math.max(0, Math.floor((now - t) / 60_000));
  if (m < 1) return 'hace un momento';
  if (m < 60) return `hace ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  return `hace ${d} ${plural(d, 'día', 'días')}`;
}

/** Tras un refresco de la lista: si falla se conserva la última buena (textos de decisiones). */
export function nextDecisions<T>(prev: T | null, f: Fetched<T>) {
  return nextList(prev, f, { kept: LIST_FAIL_KEPT, never: LIST_FAIL_NEVER });
}
